import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s,
  authHeaders,
  buildODataUrl,
  Toast,
  newRecordButtonStyle,
  getODataRows,
  resolveODataTotalCount,
  toGridSortField,
  toODataSortField,
  exportToExcel,
  ExcelIcon,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  buildFilterClause, unwrapLookupItems,
  ColumnFilterPopover, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './CustomersPage.css'; // c360-filter, c360-chooser sınıfları ortak

const API_BASE = '/api/prices';
const PRICE_LOOKUP_PAGE = 20; // ürün/müşteri picker sayfa boyutu (sonsuz kaydırma)

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const COLUMN_FILTER_META = {
  productLabel: { type: 'asyncSelect', odataField: 'ProductId', valueFormat: 'guid', fetchKey: 'product' },
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  customerLabel: { type: 'asyncSelect', odataField: 'CustomerId', valueFormat: 'guid', fetchKey: 'customer' },
  customerGroupLabel: { type: 'select', odataField: 'CustomerGroupId', valueFormat: 'guid' },
  salePrice: { type: 'number', odataField: 'SalePrice' },
  buyingPrice: { type: 'number', odataField: 'BuyingPrice' },
  vatPercentage: { type: 'number', odataField: 'VatPercentage' },
  startDate: { type: 'date', odataField: 'StartDate' },
  endDate: { type: 'date', odataField: 'EndDate' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  priceComment: { type: 'text', odataField: 'PriceComment' },
};

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('tr-TR');
}

// Excel import helpers (module-level pure)
function normKey(v) { return String(v ?? '').trim().toLowerCase(); }
function pickCol(row, keys) {
  const map = {};
  Object.keys(row).forEach((k) => { map[normKey(k)] = row[k]; });
  for (const k of keys) {
    const v = map[normKey(k)];
    if (v !== undefined && v !== '') return v;
  }
  return null;
}

// Excel tarih hücreleri seri-sayı (ör. 46023) olarak gelebilir; JS Date veya
// "2026-01-01" gibi metin de olabilir. Hepsini backend'in beklediği ISO tarihe çevir.
function toISODate(v) {
  if (v === '' || v == null) return null;
  const fmt = (d) => {
    if (Number.isNaN(d.getTime())) return null;
    // toISOString() UTC'ye çevirip Türkiye'de günü geri kaydırır → yerel bileşenlerle yaz.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  if (v instanceof Date) return fmt(v);
  if (typeof v === 'number') {
    // Excel seri tarih → JS Date (25569 = 1899-12-30 ile 1970-01-01 arası gün farkı).
    // UTC gece-yarısı kur, yerel bileşenlerle oku → kayma olmaz.
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return Number.isNaN(d.getTime()) ? null : `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }
  const sv = String(v).trim();
  if (!sv) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(sv)) return sv.slice(0, 10); // zaten ISO → kaymadan geç
  const d = new Date(sv);
  return Number.isNaN(d.getTime()) ? sv : fmt(d);
}

// --- Main component -------------------------------------------------------
export function PricesPage() {
  usePageMeta('Fiyatlar', 'Ürün satış/alış/iade fiyatlarını yönetin.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [columnFilters, setColumnFilters] = useState({});
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  // Import sırasında dosyadaki kod→id eşleşmeleri sunucudan çözümlenip burada tutulur
  // (grid'in lazy-load ettiği küçük products/customers state'i import için yetersizdir).
  const importProductMap = useRef(new Map());
  const importCustomerMap = useRef(new Map());

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // Lookup'lar
  // Customer ve Product hariç tüm lookup'lar başlangıçta yüklenir
  // (vendor/group/subgroup küçük listeler). Customer 4000+, Product 8000+ →
  // fetchData'dan sonra sadece sayfada görünen id'ler için ayrıca çekilir.
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendors/Select', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/api/customergroups/Select', { headers }).then(r => r.ok ? r.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then(r => r.ok ? r.json() : []),
    ]).then(([v, cg, csg]) => {
      setVendors(unwrapLookupItems(v));
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);


  const productMap = useMemo(() => new Map(products.map(p => [p.id || p.Id, lookupLabel(p)])), [products]);
  const vendorMap = useMemo(() => new Map(vendors.map(v => [v.id || v.Id, lookupLabel(v)])), [vendors]);
  const customerMap = useMemo(() => new Map(customers.map(c => [c.id || c.Id, lookupLabel(c)])), [customers]);
  const customerGroupMap = useMemo(() => new Map(customerGroups.map(g => [g.id || g.Id, lookupLabel(g)])), [customerGroups]);
  const customerSubGroupMap = useMemo(() => new Map(customerSubGroups.map(g => [g.id || g.Id, lookupLabel(g)])), [customerSubGroups]);

  // Filter options (select tipi kolonlar için lookup'lardan üret)
  const productFilterOpts = useMemo(() => products.map(p => ({ value: p.id || p.Id, label: lookupLabel(p) })), [products]);
  const vendorFilterOpts = useMemo(() => vendors.map(v => ({ value: v.id || v.Id, label: lookupLabel(v) })), [vendors]);
  const customerFilterOpts = useMemo(() => customers.map(c => ({ value: c.id || c.Id, label: lookupLabel(c) })), [customers]);
  const customerGroupFilterOpts = useMemo(() => customerGroups.map(g => ({ value: g.id || g.Id, label: lookupLabel(g) })), [customerGroups]);

  const FILTER_OPTIONS_BY_COLUMN = useMemo(() => ({
    productLabel: productFilterOpts,
    vendorLabel: vendorFilterOpts,
    customerLabel: customerFilterOpts,
    customerGroupLabel: customerGroupFilterOpts,
    status: STATUS_OPTIONS,
  }), [productFilterOpts, vendorFilterOpts, customerFilterOpts, customerGroupFilterOpts]);

  // Büyük lookup'lar için sunucu taraflı, sayfalı arama (20'şer; aşağı kaydırınca sonraki 20).
  const searchProducts = useCallback(async (query, skip = 0) => {
    const term = (query || '').replace(/'/g, "''").toLowerCase();
    const filter = `contains(tolower(TextId),'${term}') or contains(tolower(Description),'${term}')`;
    // light=1 → ProductImage'ı dışla; aksi halde satır başına ~1.7MB base64 görsel taşınır.
    const url = `/api/products?$top=${PRICE_LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId&light=1&$filter=${encodeURIComponent(filter)}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    return getODataRows(data).map((p) => ({ value: p.id || p.Id, label: lookupLabel(p) }));
  }, []);

  const searchCustomers = useCallback(async (query, skip = 0) => {
    const term = (query || '').replace(/'/g, "''").toLowerCase();
    const filter = `contains(tolower(TextId),'${term}') or contains(tolower(Title),'${term}')`;
    const url = `/api/customers?$top=${PRICE_LOOKUP_PAGE}&$skip=${skip}&$orderby=TextId&$filter=${encodeURIComponent(filter)}`;
    const r = await fetch(url, { headers: authHeaders() });
    if (!r.ok) return [];
    const data = await r.json();
    return getODataRows(data).map((c) => ({ value: c.id || c.Id, label: lookupLabel(c) }));
  }, []);

  // $select: yalnız GÖRÜNÜR kolonların OData kaynak alanlarını çek (Column Chooser ile entegre).
  // 'Id' satır anahtarı + /prices/:id nav; ProductId/CustomerId ürün/müşteri etiketleri fetchData
  // içinde lazy çekildiği (haveProductIds/haveCustomerIds) için görünürlükten bağımsız her zaman
  // gerekli; CustomerSubGroupId kolonu (Alt Grup etiketi) COLUMN_FILTER_META'da olmadığından elle
  // eklenir. sortField sunucuda projeksiyondan önce uygulanır → sıralama alanı da eklenir.
  const listSelectFields = useMemo(() => {
    const set = new Set(['Id', 'ProductId', 'CustomerId', 'CustomerSubGroupId']);
    for (const [colId, meta] of Object.entries(COLUMN_FILTER_META)) {
      if (meta.odataField && columnVisibility[colId] !== false) set.add(meta.odataField);
    }
    if (sortField) set.add(sortField);
    return Array.from(set);
  }, [columnVisibility, sortField]);

  const fetchData = useCallback(async () => {
    if (!lookupsLoaded) return;
    setLoading(true);
    try {
      const clauses = [];
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        clauses.push(`(contains(tolower(PriceComment),'${q}') or contains(tolower(Description),'${q}'))`);
      }
      for (const [field, filter] of Object.entries(columnFilters)) {
        const meta = COLUMN_FILTER_META[field];
        const clause = buildFilterClause(meta, filter);
        if (clause) clauses.push(clause);
      }
      const filterParams = clauses.join(' and ');
      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams, select: listSelectFields });
      const headers = authHeaders();

      // 1) Önce fiyatları çek
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Veri alınamadı');
      const data = await res.json();
      const pageRows = getODataRows(data);

      // 2) Sayfadaki productId/customerId'lerden state'te olmayanları topla
      const haveProductIds = new Set(products.map(p => p.id || p.Id));
      const haveCustomerIds = new Set(customers.map(c => c.id || c.Id));
      const needProductIds = Array.from(new Set(
        pageRows.map(r => r.productId || r.ProductId).filter(id => id && !haveProductIds.has(id))
      ));
      const needCustomerIds = Array.from(new Set(
        pageRows.map(r => r.customerId || r.CustomerId).filter(id => id && !haveCustomerIds.has(id))
      ));

      // 3) Eksik product ve customer'ları parallel çek
      const fetchByIds = async (apiBase, ids) => {
        if (ids.length === 0) return [];
        const filter = ids.map(id => `Id eq ${id}`).join(' or ');
        // light=1 → ProductImage'ı dışla (grid'de görsel kullanılmıyor); küçük $top'ta
        // (≤100) aksi halde satır başına ~1.7MB base64 taşınır. Customer'da görsel yok → no-op.
        const u = `${apiBase}?$filter=${encodeURIComponent(filter)}&$top=${ids.length}&light=1`;
        const r = await fetch(u, { headers });
        if (!r.ok) return [];
        const d = await r.json();
        return unwrapLookupItems(d);
      };
      const [newProducts, newCustomers, totalCount] = await Promise.all([
        fetchByIds('/api/products', needProductIds),
        fetchByIds('/api/customers', needCustomerIds),
        resolveODataTotalCount({ data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers }),
      ]);

      // 4) Hepsi geldi, state'i atomik güncelle → tablo direkt label ile render
      if (newProducts.length > 0) {
        setProducts(prev => {
          const known = new Set(prev.map(p => p.id || p.Id));
          const fresh = newProducts.filter(p => !known.has(p.id || p.Id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      }
      if (newCustomers.length > 0) {
        setCustomers(prev => {
          const known = new Set(prev.map(c => c.id || c.Id));
          const fresh = newCustomers.filter(c => !known.has(c.id || c.Id));
          return fresh.length > 0 ? [...prev, ...fresh] : prev;
        });
      }
      setRows(pageRows);
      setTotalCount(totalCount);
    } catch (e) {
      showToast('error', e.message || 'Veri alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [columnFilters, filterText, sortField, sortDir, pageSize, currentPage, lookupsLoaded, products, customers, listSelectFields]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Navigasyon (kayıt görüntüleme/düzenleme tam-sayfa /prices/:id'e gider) ---
  const openNew = () => navigate('/prices/new');
  const openDetail = useCallback((r) => navigate(`/prices/${r.id || r.Id}`, { state: { row: r } }), [navigate]);
  const openEditRow = useCallback((r) => navigate(`/prices/${r.id || r.Id}?edit=1`, { state: { row: r } }), [navigate]);

  // --- Import: Wizard (Excel/CSV → BulkImport) ---------------------------
  // Dosyadaki kodları (ürün/müşteri 8000+/4000+) satır-satır çevirmeden ÖNCE sunucudan
  // toplu çözümle. Grid'in lazy-load ettiği state import için yetersiz → "Ürün eşleştirilemedi".
  const fetchIdsByTextIds = useCallback(async (apiBase, textIds, light) => {
    const map = new Map();
    const CHUNK = 50;
    const headers = authHeaders();
    for (let i = 0; i < textIds.length; i += CHUNK) {
      const chunk = textIds.slice(i, i + CHUNK);
      const filter = chunk.map((t) => `TextId eq '${String(t).replace(/'/g, "''")}'`).join(' or ');
      const u = `${apiBase}?$filter=${encodeURIComponent(filter)}&$top=${chunk.length}${light ? '&light=1' : ''}`;
      const r = await fetch(u, { headers });
      if (!r.ok) continue;
      const d = await r.json();
      unwrapLookupItems(d).forEach((item) => {
        const key = normKey(item.textId || item.TextId);
        if (key) map.set(key, item.id || item.Id);
      });
    }
    return map;
  }, []);

  const preparePriceImport = useCallback(async (rawRows) => {
    const prodKeys = new Set();
    const custKeys = new Set();
    rawRows.forEach((row) => {
      const p = pickCol(row, ['ProductTextId', 'Ürün Kodu', 'UrunKodu', 'UrunTextId', 'TextId']);
      if (p) prodKeys.add(normKey(p));
      const c = pickCol(row, ['CustomerTextId', 'Müşteri Kodu', 'MusteriTextId']);
      if (c) custKeys.add(normKey(c));
    });
    const [pm, cm] = await Promise.all([
      fetchIdsByTextIds('/api/products', [...prodKeys], true),
      fetchIdsByTextIds('/api/customers', [...custKeys], false),
    ]);
    importProductMap.current = pm;
    importCustomerMap.current = cm;
  }, [fetchIdsByTextIds]);

  const rowToPricePayload = useCallback((row) => {
    // Vendor listesi başlangıçta tam yüklü → state yeterli. Ürün/müşteri için prepare ile
    // sunucudan çözümlenen harita önceliklidir, yoksa state'teki kısmi listeye düşer.
    const productByText = new Map(products.map((p) => [normKey(p.textId || p.TextId), p.id || p.Id]));
    const vendorByText = new Map(vendors.map((v) => [normKey(v.textId || v.TextId), v.id || v.Id]));
    const customerByText = new Map(customers.map((c) => [normKey(c.textId || c.TextId), c.id || c.Id]));
    const productKey = pickCol(row, ['ProductTextId', 'Ürün Kodu', 'UrunKodu', 'UrunTextId', 'TextId']);
    const vendorKey = pickCol(row, ['VendorTextId', 'Distribütör Kodu', 'Tedarikçi Kodu', 'DistributorTextId', 'TedarikciTextId']);
    const customerKey = pickCol(row, ['CustomerTextId', 'Müşteri Kodu', 'MusteriTextId']);
    const resolveProduct = (k) => importProductMap.current.get(k) || productByText.get(k) || null;
    const resolveCustomer = (k) => importCustomerMap.current.get(k) || customerByText.get(k) || null;
    const toNum = (v) => { const n = Number(v); return Number.isNaN(n) ? 0 : n; };
    const toNumOrNull = (v) => { if (v === '' || v == null) return null; const n = Number(v); return Number.isNaN(n) ? null : n; };
    return {
      productId: productKey ? resolveProduct(normKey(productKey)) : null,
      vendorId: vendorKey ? vendorByText.get(normKey(vendorKey)) || null : null,
      customerId: customerKey ? resolveCustomer(normKey(customerKey)) : null,
      buyingPrice: toNum(pickCol(row, ['BuyingPrice', 'Alış Fiyatı', 'AlisFiyati'])),
      salePrice: toNum(pickCol(row, ['SalePrice', 'Satış Fiyatı', 'SatisFiyati'])),
      returnPrice: toNum(pickCol(row, ['ReturnPrice', 'İade Fiyatı', 'IadeFiyati'])),
      recommendedPrice: toNumOrNull(pickCol(row, ['RecommendedPrice', 'Tavsiye Fiyat', 'TavsiyeFiyat'])),
      vatPercentage: toNum(pickCol(row, ['VatPercentage', 'KDV', 'KDV%', 'KDV Yüzdesi'])),
      startDate: toISODate(pickCol(row, ['StartDate', 'Başlangıç', 'BaslangicTarihi'])),
      endDate: toISODate(pickCol(row, ['EndDate', 'Bitiş', 'BitisTarihi'])),
      status: 1,
      priceComment: pickCol(row, ['PriceComment', 'Yorum']) || null,
      description: pickCol(row, ['Description', 'Açıklama']) || null,
    };
  }, [products, vendors, customers]);
  const validatePriceRow = useCallback((p) => {
    if (!p.productId) return 'Ürün eşleştirilemedi (ProductTextId / Ürün Kodu)';
    if (!(p.salePrice > 0)) return 'Satış Fiyatı zorunlu (>0)';
    return null;
  }, []);
  const submitPriceBatch = useCallback(async (payloads) => {
    const res = await fetch(`${API_BASE}/BulkImport`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify(payloads),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      const msg = body?.message || body?.Message || `Sunucu ${res.status}`;
      return { ok: 0, fail: payloads.length, errors: [msg] };
    }
    const result = body?.data ?? body?.Data ?? {};
    const ok = result.inserted ?? result.Inserted ?? 0;
    const fail = result.skipped ?? result.Skipped ?? Math.max(0, payloads.length - ok);
    const errors = result.errors ?? result.Errors ?? [];
    return { ok, fail, errors };
  }, []);
  const downloadImportTemplate = async () => {
    // Örnek Ürün Kodu, başka bir tenant'ın kodu sızmasın diye sabit yazılmaz;
    // login olan tenant'ın gerçek bir ürününden çekilir (liste OData ile tenant-filtreli).
    let sampleProductCode = '';
    try {
      const r = await fetch('/api/products?$top=1&$orderby=TextId&light=1', { headers: authHeaders() });
      if (r.ok) {
        const first = getODataRows(await r.json())[0];
        sampleProductCode = first?.TextId ?? first?.textId ?? '';
      }
    } catch { /* ürün yoksa örnek kodu boş bırak — kullanıcı kendi kodunu yazar */ }
    const sample = [{
      ProductTextId: sampleProductCode,
      VendorTextId: '',
      CustomerTextId: '',
      BuyingPrice: 12,
      SalePrice: 15.5,
      ReturnPrice: 14,
      RecommendedPrice: 16,
      VatPercentage: 18,
      StartDate: '2026-01-01',
      EndDate: '2027-01-01',
      PriceComment: 'Örnek',
    }];
    exportToExcel(sample, null, 'fiyat_sablon');
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id || selected.Id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Fiyat pasifleştirildi');
      setDeleteConfirm(false); setSelected(null); fetchData();
    } catch (e) {
      showToast('error', e.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  // --- Columns ------------------------------------------------------------
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor((r) => productMap.get(r.productId || r.ProductId) || '—', {
      id: 'productLabel', header: 'Ürün',
      cell: (info) => <span title={info.getValue()}>{info.getValue()}</span>,
    }),
    columnHelper.accessor((r) => vendorMap.get(r.vendorId || r.VendorId) || '—', {
      id: 'vendorLabel', header: 'Distribütör',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('salePrice', {
      id: 'salePrice', header: 'Satış',
      cell: (info) => <span style={{ fontWeight: 600 }}>{fmtMoney(info.getValue())}</span>,
    }),
    columnHelper.accessor('buyingPrice', {
      id: 'buyingPrice', header: 'Alış',
      cell: (info) => fmtMoney(info.getValue()),
    }),
    columnHelper.accessor('vatPercentage', {
      id: 'vatPercentage', header: 'KDV %',
      cell: (info) => fmtMoney(info.getValue()),
    }),
    columnHelper.accessor((r) => customerMap.get(r.customerId || r.CustomerId) || '—', {
      id: 'customerLabel', header: 'Müşteri',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((r) => customerGroupMap.get(r.customerGroupId || r.CustomerGroupId) || '—', {
      id: 'customerGroupLabel', header: 'Müşteri Grubu',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((r) => customerSubGroupMap.get(r.customerSubGroupId || r.CustomerSubGroupId) || '—', {
      id: 'customerSubGroupLabel', header: 'Alt Grup',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('startDate', {
      id: 'startDate', header: 'Başlangıç',
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('endDate', {
      id: 'endDate', header: 'Bitiş',
      cell: (info) => fmtDate(info.getValue()),
    }),
    columnHelper.accessor('status', {
      id: 'status', header: 'Durum',
      cell: (info) => {
        const meta = STATUS_META[info.getValue()];
        return meta ? <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge> : '—';
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <button title="Görüntüle" style={iconBtn('var(--accent)')} onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button title="Düzenle" style={iconBtn('var(--warn)')} onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button title="Pasifleştir" style={iconBtn('#E53E3E')} onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </div>
      ),
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [productMap, vendorMap, customerMap, customerGroupMap, customerSubGroupMap, openDetail, openEditRow, canEdit, canDelete]);

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting: [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }],
      columnVisibility,
      pagination: { pageIndex: currentPage - 1, pageSize },
    },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater([{ id: toGridSortField(sortField), desc: sortDir === 'desc' }]) : updater;
      const first = next[0];
      if (!first) return;
      setSortField(toODataSortField(first.id));
      setSortDir(first.desc ? 'desc' : 'asc');
      setCurrentPage(1);
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil(totalCount / pageSize)),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id || row.Id),
  });

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Yorum veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterText(''); setFilterInput(''); setCurrentPage(1); }}>Temizle</button>
      )}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <button style={s.btnSecondary} onClick={downloadImportTemplate}>Şablon İndir</button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel'e Aktar"
          aria-label="Excel'e Aktar"
          onClick={() => exportToExcel(rows, columns, 'fiyatlar')}
        >
          <ExcelIcon size={16} />
        </button>
        <button
          type="button"
          style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          title="Excel/CSV'den İçe Aktar"
          aria-label="Excel/CSV'den İçe Aktar"
          onClick={() => setImportOpen(true)}
        >
          <UploadIcon size={16} />
        </button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  // --- Render --------------------------------------------------------------
  return (
    <div style={s.container}>
      <ListPageShell
        title="Fiyatlar"
        subtitle="Ürün satış/alış/iade fiyatlarını filtreleyin, sıralayın ve yönetin."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Fiyat</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <div className="c360-table-wrap">
          <table className="c360-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortState = header.column.getIsSorted();
                    const filterMeta = COLUMN_FILTER_META[header.column.id];
                    return (
                      <th
                        key={header.id}
                        className={canSort ? 'is-sortable' : undefined}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        {header.isPlaceholder ? null : (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <span className="c360-sort-icon">{sortState === 'asc' ? '▲' : sortState === 'desc' ? '▼' : ''}</span>
                            )}
                            {filterMeta && (
                              <ColumnFilterPopover
                                column={header.column}
                                filter={columnFilters[header.column.id]}
                                filterType={filterMeta.type}
                                options={filterMeta.options || FILTER_OPTIONS_BY_COLUMN[header.column.id]}
                                fetchOptions={filterMeta.fetchKey === 'product' ? searchProducts : filterMeta.fetchKey === 'customer' ? searchCustomers : undefined}
                                resolveLabel={filterMeta.fetchKey === 'product' ? ((id) => productMap.get(id)) : filterMeta.fetchKey === 'customer' ? ((id) => customerMap.get(id)) : undefined}
                                onApply={(next) => {
                                  if ((filterMeta.type === 'select' || filterMeta.type === 'asyncSelect') && (next.value === '' || next.value == null)) {
                                    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[header.column.id]; return copy; });
                                  } else {
                                    setColumnFilters((prev) => ({ ...prev, [header.column.id]: next }));
                                  }
                                  setCurrentPage(1);
                                }}
                                onClear={() => {
                                  setColumnFilters((prev) => { const copy = { ...prev }; delete copy[header.column.id]; return copy; });
                                  setCurrentPage(1);
                                }}
                              />
                            )}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {(() => {
                const tanstackRows = table.getRowModel().rows;
                if (tanstackRows.length === 0) {
                  return (
                    <tr className="is-empty">
                      <td colSpan={columns.length}>{loading ? 'Yükleniyor...' : 'Kayit bulunamadi'}</td>
                    </tr>
                  );
                }
                return tanstackRows.map((trow) => {
                  const isSelectedRow = selected?.id === trow.original.id;
                  return (
                    <tr
                      key={trow.id}
                      className={isSelectedRow ? 'is-selected' : undefined}
                      onClick={() => setSelected(trow.original)}
                      onDoubleClick={() => openDetail(trow.original)}
                    >
                      {trow.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
          {loading && table.getRowModel().rows.length > 0 && <div style={s.loadingOverlay}>Yükleniyor...</div>}
        </div>
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Fiyatı Pasifleştir</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(false)}>x</button>
            </div>
            <div style={s.modalBody}>
              <p>Bu fiyat tanımını pasifleştirmek istediğinize emin misiniz? (Soft delete — Status 0 olur.)</p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Vazgeç</button>
              <button style={{ ...s.btnPrimary, background: '#E53E3E' }} onClick={handleDelete}>Pasifleştir</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) fetchData(); }}
        title="Fiyat İçe Aktar"
        helpText={(
          <>
            Şablondaki kolon başlıklarını koruyun. Zorunlu alanlar: <strong>Ürün Kodu (ProductTextId)</strong>
            ve <strong>Satış Fiyatı (SalePrice)</strong>. Müşteri/Distribütör kodu opsiyonel — boşsa jenerik fiyat olur.
          </>
        )}
        prepare={preparePriceImport}
        rowToPayload={rowToPricePayload}
        validateRow={validatePriceRow}
        submitBatch={submitPriceBatch}
        downloadTemplate={downloadImportTemplate}
      />

      <Toast toast={toast} />
    </div>
  );
}

function iconBtn(color) {
  return {
    border: 'none', borderRadius: 6, background: color, color: '#fff',
    cursor: 'pointer', padding: '6px 8px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };
}
